import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/shared";

const MAX_OVERLAY_SHADOW = "0 0 0 9999px rgba(17, 24, 39, 0.45)";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const readElementMetrics = (element) => {
  const rect = element.getBoundingClientRect();
  const styles = window.getComputedStyle(element);
  const width =
    rect.width ||
    element.offsetWidth ||
    element.clientWidth ||
    parseFloat(styles.width) ||
    0;
  const height =
    rect.height ||
    element.offsetHeight ||
    element.clientHeight ||
    parseFloat(styles.height) ||
    0;

  return {
    element,
    rect,
    styles,
    width,
    height,
    area: width * height,
  };
};

const getTargetElement = (selector) => {
  if (!selector || typeof document === "undefined") return null;

  const candidates = Array.from(document.querySelectorAll(selector));
  if (!candidates.length) return null;

  const visibleCandidates = candidates
    .map((element) => {
      const metrics = readElementMetrics(element);
      const isVisible =
        metrics.styles.display !== "none" &&
        metrics.styles.visibility !== "hidden" &&
        Number(metrics.styles.opacity || 1) > 0 &&
        metrics.width > 8 &&
        metrics.height > 8;
      return {
        ...metrics,
        isVisible,
      };
    })
    .filter((item) => item.isVisible);

  if (!visibleCandidates.length) return null;

  visibleCandidates.sort((a, b) => b.area - a.area);
  return visibleCandidates[0];
};

function getValidStepIndex(steps, fromIndex = 0) {
  for (let idx = fromIndex; idx < steps.length; idx += 1) {
    const selector = steps[idx]?.selector;
    if (!selector) continue;
    const element = getTargetElement(selector);
    if (element) return idx;
  }
  return -1;
}

export function UiTour({
  visible,
  steps = [],
  onClose,
  onSkip,
  onComplete,
  onStepChange,
}) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!visible) return;
    const firstValid = getValidStepIndex(steps, 0);
    setStepIndex(firstValid >= 0 ? firstValid : 0);
  }, [visible, steps]);

  const activeStep = steps[stepIndex];

  useEffect(() => {
    if (!visible || !activeStep) return;
    onStepChange?.(activeStep);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("attune:tutorial-step-change", {
          detail: { stepId: activeStep.id },
        })
      );
    }
  }, [visible, activeStep, onStepChange]);

  const targetRect = useMemo(() => {
    if (!visible || !activeStep?.selector) return null;
    const targetMetrics = getTargetElement(activeStep.selector);
    if (!targetMetrics) return null;
    const { rect, width: targetWidth, height: targetHeight } = targetMetrics;
    const isSidebarStep = activeStep.id === "sidebar";
    const isDesktopSidebar = isSidebarStep && window.innerWidth >= 768;
    const expandedDesktopSidebarWidth = 240; // matches Tailwind w-60
    const normalizedWidth = isDesktopSidebar
      ? Math.max(targetWidth, expandedDesktopSidebarWidth)
      : targetWidth;
    return {
      top: rect.top,
      left: rect.left,
      width: normalizedWidth,
      height: targetHeight,
      bottom: rect.top + targetHeight,
      right: rect.left + normalizedWidth,
    };
  }, [visible, activeStep, stepIndex]);

  useEffect(() => {
    if (!visible) return;
    if (!activeStep) return;
    if (targetRect) return;

    const nextValid = getValidStepIndex(steps, stepIndex + 1);
    if (nextValid >= 0) {
      setStepIndex(nextValid);
      return;
    }

    onComplete?.();
  }, [visible, activeStep, targetRect, steps, stepIndex, onComplete]);

  if (!visible || !activeStep || !targetRect) {
    return null;
  }

  const panelWidth = 340;
  const topCandidate = targetRect.bottom + 12;
  const leftCandidate = targetRect.left;
  const panelTop = clamp(topCandidate, 12, window.innerHeight - 220);
  const panelLeft = clamp(leftCandidate, 12, window.innerWidth - panelWidth - 12);
  const isLastStep = stepIndex === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[1200]">
      <div
        className="absolute rounded-lg border-2 border-primary-500 bg-transparent pointer-events-none"
        style={{
          top: `${targetRect.top - 6}px`,
          left: `${targetRect.left - 6}px`,
          width: `${targetRect.width + 12}px`,
          height: `${targetRect.height + 12}px`,
          boxShadow: MAX_OVERLAY_SHADOW,
        }}
      />

      <div
        className="absolute w-[340px] rounded-xl border border-gray-200 bg-white p-4 shadow-2xl"
        style={{ top: `${panelTop}px`, left: `${panelLeft}px` }}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-primary-700">
            Step {stepIndex + 1} of {steps.length}
          </div>
          <Button
            type="alternative"
            size="sm"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
        <h3 className="text-base font-semibold text-gray-900">{activeStep.title}</h3>
        <p className="mt-2 text-sm text-gray-600">{activeStep.description}</p>

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button
            type="alternative"
            size="sm"
            onClick={onSkip}
          >
            Skip Tour
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="alternative"
              size="sm"
              onClick={() => {
                const previousValid = [...Array(stepIndex).keys()]
                  .reverse()
                  .find((idx) => {
                    const selector = steps[idx]?.selector;
                    return selector && getTargetElement(selector);
                  });
                if (typeof previousValid === "number") {
                  setStepIndex(previousValid);
                }
              }}
              disabled={stepIndex === 0}
            >
              Back
            </Button>
            <Button
              type="primary"
              size="sm"
              onClick={() => {
                if (isLastStep) {
                  onComplete?.();
                  return;
                }
                const nextValid = getValidStepIndex(steps, stepIndex + 1);
                if (nextValid >= 0) {
                  setStepIndex(nextValid);
                  return;
                }
                onComplete?.();
              }}
            >
              {isLastStep ? "Finish Tour" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UiTour;
